import React, { useEffect, useState, Fragment, useRef } from 'react';
import { cloneDeep } from 'lodash';

import { AAMBeaconModal, AAMBeaconButton } from 'components/shared/Beacon';
import CostBasisOption from './CostBasisOption';
import infoHelpIcon from 'assets/icons/info-help-icon-filled.svg';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';
import './EditModal.scss';
import {
  AVERAGE_COST,
  AVERAGE_COST_FIRST_IN_FIRST_OUT,
  brokerageCostBasisElections,
  costBasisMethodOptions,
  SPECIFIC_LOT_IDENTIFICATION
} from 'services/CostBasisMethod';
import ToolTip from 'components/shared/ToolTip/ToolTip';
import Content from 'services/Content';

const EditModal = ({
                     openModal = false,
                     account,
                     accountType,
                     hasMutualFund,
                     submitCostBasis,
                     currentCostBasis,
                     isCustomerWebView,
                     isSalesforceRepEnabled,
                     requestStatus
                   }) => {
  if (!account) {
    return null;
  }
  const [updated, setUpdated] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [costBasisTypeIdsList, setCostBasisTypeIdsList] = useState({});
  const [disabled, setDisabled] = useState(true);
  const [isAverageCost, setIsAverageCost] = useState(false);
  const [movedOutOfAvgCost, setMovedOutOfAvgCost] = useState(false);
  const [brokerageMFChanged, setBrokerageMFChanged] = useState(false);

  const modal = useRef(null);
  useEffect(() => {
    setUpdated(true);
    setCostBasisTypeIdsList(cloneDeep(currentCostBasis));
    modal.current.scrollTo({
      top: 0
    });
  }, [account, currentCostBasis]);

  useEffect(() => {
    if (
      requestStatus.updateCustomerPreferences === 'RECEIVED' ||
      requestStatus.updateCustomerPreferences === 'ERROR'
    ) {
      closeEditModal();
      setShowSpinner(false);
    }

    if (requestStatus.updateCustomerPreferences === 'REQUESTED') {
      setShowSpinner(true);
    }
  }, [requestStatus]);

  const onValueChange = ({ value, curIndex }) => {
    const costBasisValues = costBasisTypeIdsList;
    costBasisValues[`${curIndex}`] = {
      value: value,
      label: account?.isBrokerage
        ? brokerageCostBasisElections.find(
          (election) => election.value === value
        )?.label
        : costBasisMethodOptions.find((election) => election.value === value)
          ?.label
    };
    if (account?.isBrokerage) {
      setIsAverageCost(
        (costBasisValues.mutualFundCostBasisType.value !==
          currentCostBasis.mutualFundCostBasisType.value &&
          costBasisValues.mutualFundCostBasisType.value ===
          AVERAGE_COST_FIRST_IN_FIRST_OUT) ||
        (costBasisValues.dripCostBasisType.value !==
          currentCostBasis.dripCostBasisType.value &&
          costBasisValues.dripCostBasisType.value ===
          AVERAGE_COST_FIRST_IN_FIRST_OUT)
      );
      setDisabled(
        costBasisValues.mutualFundCostBasisType.value ===
        currentCostBasis.mutualFundCostBasisType.value &&
        costBasisValues.otherSecurityCostBasisType.value ===
        currentCostBasis.otherSecurityCostBasisType.value &&
        costBasisValues.dripCostBasisType.value ===
        currentCostBasis.dripCostBasisType.value
      );
      setBrokerageMFChanged(
        costBasisValues.mutualFundCostBasisType.value !==
        currentCostBasis.mutualFundCostBasisType.value
      );
    } else {
      setIsAverageCost(
        costBasisValues[`${curIndex}`].value !==
        currentCostBasis[`${curIndex}`].value &&
        costBasisValues[`${curIndex}`].value === AVERAGE_COST
      );
      setMovedOutOfAvgCost(
        costBasisValues[`${curIndex}`].value !==
        currentCostBasis[`${curIndex}`].value &&
        currentCostBasis[`${curIndex}`].value === AVERAGE_COST &&
        costBasisValues[`${curIndex}`].value !== AVERAGE_COST);
      setDisabled(
        costBasisValues[`${curIndex}`].value ===
        currentCostBasis[`${curIndex}`].value ||
        costBasisValues[`${curIndex}`].value === SPECIFIC_LOT_IDENTIFICATION
      );
    }
    setCostBasisTypeIdsList(costBasisValues);
  };

  const closeEditModal = () => {
    handleCancel('cost-basis-edit-modal');
  };
  const handleSubmit = (isAverageCost, movedOutOfAvgCost, averageCostText) => {
    if (isAverageCost || movedOutOfAvgCost) {
      openBeaconModal('average-cost-basis-modal-v2');
    } else {
      setCostBasisTypeIdsList({});
      submitCostBasis({
        account,
        costBasisTypeIdsList,
        averageCostText
      });
    }
  };

  const handleCancel = (selector) => {
    const modal = document.getElementById(selector) ?? null;
    if (modal !== null) {
      modal.close();
    }
  };

  const openBeaconModal = (selector) => {
    const modal = document.getElementById(selector);
    if (modal !== null) modal.show();
  };

  const averageCostMessage = account?.isBrokerage
    ? 'For account(s) that I have selected Average Cost, I agree to select the average cost method as the covered shares cost basis method applicable to the selected account(s). I understand the tax consequences of selecting this cost basis method and had the opportunity to consult with my tax advisor before I made this selection. Once a sale or other disposition has occurred with respect to a fund, I understand that I cannot revoke the average cost method for shares held in that fund prior to the change for which the average cost basis method has locked in due to a prior sale and any new method I selected would only be applied to shares acquired after the change.'
    : isAverageCost
      ? 'I agree to select the average cost method as the covered shares cost basis method applicable to the selected account(s). I understand the tax consequences of selecting this cost basis method and had the opportunity to consult with my tax advisor before I made this selection. Once a sale or other disposition has occurred with respect to a fund, I understand that I cannot revoke the average cost method for that fund, and any new method I selected would only be applied to shares acquired after the change.'
      : movedOutOfAvgCost
        ? 'For account(s) that I have changed from Average Cost to another method, I agree to change my covered shares cost basis method for the selected account(s) from Average Cost to the method(s) that I have selected. I understand that this change will be treated as a revocation of the Average Cost method for all covered securities in the selected account(s) if revocation is permissible under the applicable tax regulations; and I understand that if revocation is not permissible under the applicable tax regulations, this change will be applied only to shares acquired after I change from the Average Cost method. I understand the tax consequences of making this change and had the opportunity to consult with my tax advisor before I made this change in method.'
        : '';

  const userAgreementText = account?.isBrokerage
    ? 'IN_AVERAGE_COST_LEGAL_TEXT'
    : averageCostMessage;

  return (
    <Fragment>
      <AAMBeaconModal
        id={`average-cost-basis-modal-v2`}
        className="average-cost-disclosure-modal average-cost-basis-modal-v2"
        size={'normal'}
        title="Average Cost User Agreement">
        <div className={'cost-basis-content'}>
          <h2 className="cost-basis-edit-modal-title-primary">
            Average Cost User Agreement
          </h2>
          <div className="cost-basis-edit-modal-body-content">
            {averageCostMessage}
          </div>
          <div className="cost-basis-update-options">
            <div />
            <div
              className="cost-basis-update-button-container"
              slot="action-zone">
              <AAMBeaconButton
                variant="tertiary"
                scale="jumbo"
                onBeaconEventClick={() => {
                  handleCancel('average-cost-basis-modal-v2');
                }}
              >
                Cancel
              </AAMBeaconButton>
              <AAMBeaconButton
                variant="primary"
                scale="normal"
                onBeaconEventClick={() => {
                  setIsAverageCost(false);
                  setMovedOutOfAvgCost(false);
                  handleSubmit(false, false, userAgreementText);
                  handleCancel('average-cost-basis-modal-v2');
                }}
              >
                Confirm
              </AAMBeaconButton>
            </div>
          </div>
        </div>
      </AAMBeaconModal>

      <AAMBeaconModal
        scale="jumbo"
        ref={modal}
        id={`cost-basis-edit-modal`}
        closeOnClickOut={false}
        onBeaconEventClose={() => setUpdated(false)}
        className="edit-cost-basis-modal-v2">
        <div className="cost-basis-v2-container">
          <div className={'cost-basis-edit-modal-title'}>
            <h2 className="cost-basis-edit-modal-title-primary b-font-weight-bold b-typography-sans-serif--headline--300">
              Update Cost Basis Method
            </h2>
            {account?.isBrokerage && (
              <div className="cost-basis-edit-modal-title-secondary">
                The cost basis method you select will update the default disposition method shown on the Holdings page.
              </div>
            )}
          </div>
          {updated ? (
            <>
              <h3 className="cost-basis-sub-title blocked_data">
                <span>{accountType}</span>
              </h3>
              <div
                className={
                  hasMutualFund
                    ? 'cost-basis-individual-funds'
                    : 'individual-funds-only'
                }>
                {!account?.isBrokerage ? (
                  <CostBasisOption
                    account={account}
                    accountInfomation={account}
                    onValueChange={onValueChange}
                    openModal={openModal}
                    curIndex={'costBasisType'}
                    costBasisTypeId={account?.costBasisTypeId}
                  />
                ) : (
                  <Fragment>
                    <CostBasisOption
                      account={{
                        ...account,
                        accountName: account.accountName + ' - Mutual Funds'
                      }}
                      accountInfomation={account}
                      onValueChange={onValueChange}
                      curIndex={'mutualFundCostBasisType'}
                      openModal={openModal}
                      costBasisTypeId={
                        account?.brokerageCostBasisMethods
                          ?.mutualFundCostBasisTypeId
                      }
                      isBrokerageMutualFund={true}
                      brokerageMFChanged={brokerageMFChanged}
                    />
                    <CostBasisOption
                      account={{
                        ...account,
                        accountName:
                          account.accountName + ' - All Other Securities'
                      }}
                      accountInfomation={account}
                      onValueChange={onValueChange}
                      curIndex={'otherSecurityCostBasisType'}
                      openModal={openModal}
                      costBasisTypeId={
                        account?.brokerageCostBasisMethods
                          ?.otherSecurityCostBasisTypeId
                      }
                    />
                    <CostBasisOption
                      account={{
                        ...account,
                        accountName: (
                          <>
                            {account.accountName} - Stocks in Dividend
                            Reinvestment Plans{' '}
                            <span className="no-wrap-cost-basis-v2">
                              <ToolTip
                                className="cost-basis-tooltip-v2"
                                tooltipLabel={
                                  <div className="cost-basis-tooltip-v2-box">
                                    <div className="cost-basis-tooltip-v2-header b-font-weight-bold b-typography-sans-serif--body--100">
                                      Stocks in Dividend Reinvestment Plans
                                    </div>
                                    <div className="cost-basis-tooltip-v2-content b-typography-sans-serif--body--100">
                                      {Content.costBasisStockDividendPlanV2}
                                    </div>
                                  </div>
                                }>
                                <img src={infoHelpIcon} alt="info" />
                              </ToolTip>
                            </span>
                          </>
                        )
                      }}
                      accountInfomation={account}
                      openModal={openModal}
                      onValueChange={onValueChange}
                      curIndex={'dripCostBasisType'}
                      isBrokerageStockReinvestment={true}
                      costBasisTypeId={
                        account?.brokerageCostBasisMethods?.dripCostBasisTypeId
                      }
                    />
                  </Fragment>
                )}
              </div>
            </>
          ) : null}
        </div>
        <div className="cost-basis-update-options">
          <div />
          <div
            className="cost-basis-update-button-container"
            slot="action-zone">
            <AAMBeaconButton
              variant="tertiary"
              onBeaconEventClick={() => {
                setCostBasisTypeIdsList({});
                handleCancel('cost-basis-edit-modal');
              }}
            >
                Cancel
            </AAMBeaconButton>
            {showSpinner && (
              <div className="loading-spinner cost-basis-v2-loading-spinner">
                <Spinner />
              </div>
            )}

            {!showSpinner && (
              <AAMBeaconButton
                id={`update-cost-basis-button`}
                variant="positive"
                onBeaconEventClick={() => {
                  handleSubmit(isAverageCost, movedOutOfAvgCost, '');
                }}
                disabled={disabled || isSalesforceRepEnabled || isCustomerWebView}
              >
                Update
              </AAMBeaconButton>
            )}
          </div>
        </div>
      </AAMBeaconModal>
    </Fragment>
  );
};

export default EditModal;
